import { useState } from 'react'
import {
  Button,
  Fa,
  FormItem,
  Modal,
  PreviewType,
  TextHeader,
  Uploader,
  showSuccessMessage,
  showErrorMessage,
  showWarningMessage,
} from '@eltoro-ui/components'
import {
  Campaignservicev1Campaign,
  Campaignservicev1OrderLine,
} from 'next-gen-sdk'
import classNames from 'classnames'
import { useAuth } from 'react-oidc-context'
import { v4 as uuid } from 'uuid'
import { useAppContext } from 'Contexts'
import {
  ErrorMessage,
  getBaseUrl,
  isValidCharLength,
  simplifyEnum,
  validCheck,
} from 'Helpers'
import {
  supportRequestBasic,
  supportRequestOrderLine,
  supportRequestCampaign,
  SupportRequestResponse,
  pollEmailTriggerStatus,
} from 'Requests'
import { SupportRequestUploadList } from './SupportRequestUploadList'
import { FeatureDisabledMessage } from '../FeatureDisabledMessage'
import { CCUsersSelector, CCUser } from '../CCUsersSelector'
import { FileRejection } from 'react-dropzone'
export type PreviewWithId = {
  id: string
  file: PreviewType
}
// If an orderLine is provided, the details will be included
export const SupportRequestModal = ({
  orderLine,
  campaign,
  handleClose,
}: {
  orderLine?: Campaignservicev1OrderLine
  campaign?: Campaignservicev1Campaign
  handleClose: () => void
}) => {
  const [description, setDescription] = useState('')
  const [files, setFiles] = useState<PreviewWithId[]>([])
  const [uploadMsgs, setUploadMsgs] = useState<
    {
      msg: string
      type: 'error' | 'warning'
      fileName: string
    }[]
  >()
  const [sendingRequest, setSendingRequest] = useState(false)
  const [errorSubmitting, setErrorSubmitting] = useState(false)
  const [selectedUsers, setSelectedUsers] = useState<CCUser[]>([])
  const { currentOrg } = useAppContext()
  const auth = useAuth()

  // Check if support requests are disabled for this org
  const isSupportRequestDisabled =
    currentOrg?.notificationsDisabled || currentOrg?.inCollections

  const handleSubmit = async () => {
    if (!currentOrg?.id || !auth.user?.profile) return
    setSendingRequest(true)

    let supportRequestResponse: SupportRequestResponse | null = null

    try {
      const copyToEmails = selectedUsers.map((user) => user.email)
      const requesterName = auth.user.profile.name || ''
      const requesterEmail = auth.user.profile.email || ''
      const orgName = currentOrg.name || ''

      if (!requesterEmail) {
        showErrorMessage(
          'Unable to submit support request.',
          'Your user email could not be determined. Please log out and log back in, or contact support@eltoro.com.',
        )
        return
      }

      // Consolidate URL construction - determine environment once
      const isProduction =
        process.env.NODE_ENV === 'production' &&
        !/dev/.test(window.location.hostname)
      const url = getBaseUrl()
      const urlBS = isProduction
        ? 'https://eltoro-backstage.api.eltoro.com'
        : 'https://eltoro-backstage.api.dev.eltoro.com'

      // Construct the org settings URL using the same base as other URLs
      const orgUrl = `${url}orgs/${currentOrg.id}/org-settings`

      // Determine which function to use based on context
      if (orderLine) {
        // Use order line specific function
        supportRequestResponse = await supportRequestOrderLine(
          description,
          currentOrg.id,
          orgName,
          requesterName,
          requesterEmail,
          orgUrl,
          `${url}campaigns/dashboard/order-lines/${orderLine.id}?org_id=${orderLine.orgId}`,
          orderLine.name || 'No name',
          `${urlBS}/order-line-management/org/${orderLine.orgId}/orderLine/${orderLine.id}?org_id=${orderLine.orgId}`,
          orderLine.id || '',
          orderLine.campaign?.id
            ? `${url}campaigns/dashboard/${orderLine.campaign.id}?org_id=${orderLine.orgId}`
            : '',
          orderLine.campaign?.name || 'No campaign',
          orderLine.status ? simplifyEnum(2, orderLine.status) : 'Unknown',
          orderLine.startTime ? orderLine.startTime.toString() : '',
          orderLine.endTime ? orderLine.endTime.toString() : '',
          orderLine.impressions?.toString() || '',
          files,
          copyToEmails,
          [requesterEmail],
        )
      } else if (campaign) {
        // Use campaign specific function
        supportRequestResponse = await supportRequestCampaign(
          description,
          currentOrg.id,
          orgName,
          requesterName,
          requesterEmail,
          orgUrl,
          `${url}campaigns/dashboard/${campaign.id}/?org_id=${campaign.orgId}`,
          campaign.name || 'No name',
          campaign.id || '',
          campaign.startTime ? campaign.startTime.toString() : '',
          campaign.endTime ? campaign.endTime.toString() : '',
          urlBS,
          files,
          copyToEmails,
          [requesterEmail],
        )
      } else {
        // Use basic support request function
        supportRequestResponse = await supportRequestBasic(
          description,
          currentOrg.id,
          orgName,
          requesterName,
          requesterEmail,
          orgUrl,
          files,
          copyToEmails,
          [requesterEmail],
        )
      }

      // Close modal immediately after submission
      setSelectedUsers([]) // Reset selected users
      handleClose()

      // Show success message and start polling
      showSuccessMessage(
        "Support request submitted! We're verifying it was sent successfully...",
        "You'll be notified once confirmed.",
      )

      // Start background job polling
      if (supportRequestResponse?.jobId) {
        try {
          const jobReport = await pollEmailTriggerStatus(
            supportRequestResponse.jobId,
          )
          if (
            jobReport.status === 'complete' &&
            jobReport.templateReports.some((report) => report.status === 'sent')
          ) {
            showSuccessMessage(
              'Support request confirmed sent!',
              'Our team will respond via email.',
            )
          } else {
            showWarningMessage(
              'Partial Success',
              'Support request submitted but delivery status unclear.',
            )
          }
        } catch (pollError) {
          showErrorMessage(
            'Support Request Verification Failed',
            'Support request was submitted, but we could not verify delivery.',
          )
        }
      }
    } catch (err: any) {
      setErrorSubmitting(true)
    } finally {
      setSendingRequest(false)
    }
  }

  const handleCheckFile = (file: PreviewType): PreviewWithId | undefined => {
    const id = uuid()
    if (
      [
        'application/zip',
        'application/x-gzip',
        'application/x-zip-compressed',
        'application/x-compressed',
        'zip',
        'text/csv',
        'video/avi',
        'video/mp4',
        'video/mpeg',
        'video/ogv',
        'video/ts',
        'video/webm',
        'video/3gp',
        'video/3g2',
        'video/mov',
        'image/jpeg',
        'image/gif',
        'image/png',
      ].includes(file.type)
    ) {
      return {
        id,
        file,
      }
    }

    setUploadMsgs((prev) => {
      return [
        ...(prev || []),
        {
          fileName: file.name,
          msg: 'is not a compatible image, video, or zip file.',
          type: 'error',
        },
      ]
    })
  }

  return (
    <Modal
      noStyles
      offClick={handleClose}
      className="SupportRequestModal tablet:min-w-[calc(100%_-_2rem)] tablet:mt-[unset] max-h-[100vh] min-w-[60%]"
      header={
        <TextHeader className="py-1" type={4}>
          Support Request
        </TextHeader>
      }
      classNameContent="flex-col gap-2 flex"
    >
      {isSupportRequestDisabled ? (
        <FeatureDisabledMessage
          featureName="Support requests"
          onClose={handleClose}
        />
      ) : (
        <>
          {sendingRequest && (
            <div className="OLSuppotRequestModal__sending bg-base absolute top-0 bottom-0 right-0 left-0 z-40 overflow-clip p-2 opacity-60">
              <Fa
                className="text-tint-khaki"
                icon="spinner"
                size={6}
                animate="spin"
              />
            </div>
          )}
          <p className="text-sm">
            Responses to this ticket will be sent via email.
          </p>
          <FormItem
            htmlFor="description"
            label={<p className="mb-2 text-lg font-bold">Request Details</p>}
            wrapperClassname="flex-col"
            errorMessage={ErrorMessage({
              fieldName: description,
              max: 2000,
              label: 'Description',
              isValid:
                validCheck(description) && isValidCharLength(description, 2000),
            })}
            valid={
              validCheck(description) && isValidCharLength(description, 2000)
            }
            counter={description.length > 0}
            overRideMax={2000}
            required
          >
            <textarea
              className="TextInput TextInput__input border-grey-50 min-h-[5rem] w-full border-2"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              maxLength={2000}
            />
          </FormItem>
          <FormItem
            htmlFor={orderLine ? 'creatives' : 'support'}
            label={<p className="mb-2 text-lg font-bold">Attachments</p>}
            wrapperClassname="flex-col"
          >
            <p className="mb-2 text-sm">
              Accepted formats: .avi .mp4 .mpeg .ogv .ts .webm .3gp .3g2 .mov
              .jpeg .gif .png .csv .zip
            </p>
            <Uploader
              accepts="support"
              onDrop={(
                files: PreviewType[],
                fileRejections?: FileRejection[],
              ) => {
                setUploadMsgs([])
                files.forEach((currentFile: PreviewType) => {
                  const fileWithType = handleCheckFile(currentFile)
                  if (fileWithType) {
                    setFiles((prev) => {
                      return [...prev, fileWithType]
                    })
                  }
                })
                fileRejections?.forEach((currentFile: FileRejection) => {
                  const errorCode = currentFile.errors[0].code
                  const errorMessage =
                    errorCode === 'file-too-large'
                      ? 'File is larger than 50MB'
                      : currentFile.errors[0].message

                  setUploadMsgs((prev) => [
                    ...(prev || []),
                    {
                      fileName: currentFile.file.name,
                      msg: errorMessage,
                      type: 'error',
                    },
                  ])
                })
              }}
            >
              <Fa
                className="mt-auto pb-1"
                icon="arrow-to-bottom"
                size={3}
                type="duotone"
              />
              <TextHeader type={6} className="pb-1 normal-case">
                Drag and drop or click to upload
              </TextHeader>
            </Uploader>
            {uploadMsgs?.map((uploadMsg, i) => (
              <div
                className={classNames(
                  'SupportRequestModal__upload-msg animate-slidedown rounded p-2 text-center font-bold',
                  {
                    'text-warning bg-warning-50': uploadMsg.type === 'warning',
                    'text-danger bg-danger-50': uploadMsg.type === 'error',
                  },
                )}
                key={`Error_${uploadMsg.fileName}_${i}`}
              >
                {uploadMsg.fileName}: {uploadMsg.msg}
              </div>
            ))}
            {files.length > 0 && (
              <SupportRequestUploadList
                files={files}
                onRemove={(fileId) => {
                  setFiles((prev) =>
                    prev.filter((prevFile) => prevFile.id !== fileId),
                  )
                }}
              />
            )}
          </FormItem>

          <p className="text-sm">File size Limit(per file): 50MB</p>
          <p className="text-sm">
            If you need to send a file type we don't currently support, you have
            two options:
          </p>
          <p className="text-sm">
            • Email us directly to{' '}
            <a
              href="mailto:support@eltoro.com"
              className="text-blue-600 underline"
            >
              support@eltoro.com
            </a>
            .
          </p>
          <p className="text-sm">
            • Submit a support ticket without attachments and reply with your
            files once an agent responds.
          </p>

          <div className="mt-4">
            <CCUsersSelector
              selectedUsers={selectedUsers}
              onSelectedUsersChange={setSelectedUsers}
            />
            <p className="py-2 text-sm">
              By default, only you and the sales rep are copied. Use the
              dropdown to add others.
            </p>
          </div>

          {errorSubmitting && (
            <div className="SupportRequestModal__error-submitting animate-slidedown bg-danger-50 rounded p-2 text-center font-semibold text-current">
              There was an error submitting your support request, please try
              again. If the error persists, please email us at{' '}
              <a href="mailto:support@eltoro.com">support@eltoro.com</a>
            </div>
          )}
          <div className="SupportRequestModal__footer-btns border-t-thin border-grey-300 mt-1 flex justify-end gap-2 pt-4">
            <Button onClick={handleClose}>Close</Button>
            <Button
              onClick={handleSubmit}
              disabled={!description || sendingRequest}
              kind="primary"
            >
              Send request
            </Button>
          </div>
        </>
      )}
    </Modal>
  )
}
